import React, { Component } from "react";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { FUNNEL_START, FUNNEL_ANSWER, FUNNEL_CONTACT } from 'utils/constants';
import logoImg from 'assets/pro_funnel/logo.png';

const CONFIG = {
  documentTitle: "Application",
  logoSrc: logoImg,
  thankYouText: "Thank you for reaching out! We'll be in touch shortly.",
};

const BLUE = '#2B6CB0';
const NAVY = '#1A3A5C';
const LIGHT_BG = '#EBF4FF';

class ProQuiz extends Component {
  constructor(props) {
    super(props);
    this.state = {
      funnelId: null,
      sessionId: null,
      currentQuestion: null,
      currentAnswer: null,
      quizState: 'loading',
      submitting: false,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      submitted: false,
      contactSubmitting: false,
    };
    this.handleSingleChoice = this.handleSingleChoice.bind(this);
    this.toggleMultiChoice = this.toggleMultiChoice.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.callAnswerEndpoint = this.callAnswerEndpoint.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    document.title = CONFIG.documentTitle;
    const funnelId = new URLSearchParams(this.props.location.search).get('funnel_id');
    fetch(FUNNEL_START, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ funnel_id: funnelId }),
    })
      .then(res => res.json())
      .then(data => {
        this.setState({
          funnelId: data.funnel_id,
          sessionId: data.session_id,
          currentQuestion: data.question,
          currentAnswer: data.question.type === 'multi' ? [] : null,
          quizState: 'in_progress',
        });
      })
      .catch(() => this.setState({ quizState: 'error' }));
  }

  callAnswerEndpoint(answer) {
    const { funnelId, sessionId, currentQuestion } = this.state;
    this.setState({ submitting: true });
    fetch(FUNNEL_ANSWER, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        funnel_id: funnelId,
        session_id: sessionId,
        question_id: currentQuestion.id,
        answer,
      }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.state === 'in_progress') {
          this.setState({
            currentQuestion: data.question,
            currentAnswer: data.question.type === 'multi' ? [] : null,
            quizState: 'in_progress',
            submitting: false,
          });
        } else {
          // All terminal states (accepted, rejected, etc.) lead to contact form
          this.setState({ quizState: 'accepted', submitting: false });
        }
      })
      .catch(() => this.setState({ submitting: false }));
  }

  handleSingleChoice(value) {
    this.callAnswerEndpoint(value);
  }

  toggleMultiChoice(value) {
    this.setState(prev => {
      const current = prev.currentAnswer || [];
      return {
        currentAnswer: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value],
      };
    });
  }

  handleContinue() {
    const { currentQuestion, currentAnswer } = this.state;
    this.callAnswerEndpoint(
      currentQuestion.type === 'multi' ? (currentAnswer || []) : (currentAnswer || '')
    );
  }

  handleSubmit() {
    const { sessionId, firstName, lastName, email, phone } = this.state;
    if (!firstName || !lastName || !email || !phone) return;
    this.setState({ contactSubmitting: true });
    fetch(FUNNEL_CONTACT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        first_name: firstName,
        last_name: lastName,
        email,
        phone_number: phone,
      }),
    })
      .then(() => this.setState({ submitted: true, contactSubmitting: false }))
      .catch(() => this.setState({ submitted: true, contactSubmitting: false }));
  }

  getMomentumProgress() {
    const { currentQuestion, quizState } = this.state;
    if (quizState === 'accepted') return 97;
    if (!currentQuestion) return 5;
    return Math.round((1 - Math.pow(0.82, currentQuestion.step)) * 96);
  }

  renderStyles() {
    return (
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .pq-root {
          font-family: 'Inter', sans-serif;
          min-height: 100vh;
          background: ${LIGHT_BG};
          display: flex;
          justify-content: center;
        }

        .pq-page {
          background: #fff;
          max-width: 480px;
          width: 100%;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .pq-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #E8EFF7;
          display: flex;
          justify-content: center;
        }

        .pq-logo {
          max-height: 44px;
          max-width: 140px;
          object-fit: contain;
        }

        .pq-progress-wrap {
          padding: 12px 24px 0;
        }

        .pq-progress-track {
          height: 6px;
          background: #D6E8F7;
          border-radius: 999px;
          overflow: hidden;
        }

        .pq-progress-fill {
          height: 100%;
          background: ${BLUE};
          border-radius: 999px;
          transition: width 0.5s ease-out;
        }

        .pq-body {
          flex: 1;
          padding: 28px 24px 16px;
          display: flex;
          flex-direction: column;
        }

        .pq-question {
          font-size: 18px;
          font-weight: 700;
          color: ${NAVY};
          text-align: center;
          margin-bottom: 6px;
          line-height: 1.4;
        }

        .pq-subtitle {
          font-size: 13px;
          color: #7A94B0;
          text-align: center;
          margin-bottom: 20px;
        }

        .pq-option {
          width: 100%;
          padding: 14px 16px;
          margin-bottom: 10px;
          background: #fff;
          border: 1.5px solid #D6E8F7;
          border-radius: 10px;
          font-size: 15px;
          color: ${NAVY};
          text-align: left;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 1px 4px rgba(43,108,176,0.06);
          transition: border-color 0.12s, background 0.12s;
        }

        .pq-option:hover {
          border-color: ${BLUE};
          background: #F0F7FF;
        }

        .pq-option.selected {
          border-color: ${BLUE};
          background: #EBF4FF;
        }

        .pq-option:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .pq-textarea {
          width: 100%;
          border: 1.5px solid #D6E8F7;
          border-radius: 10px;
          padding: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          color: ${NAVY};
          resize: none;
          outline: none;
          transition: border-color 0.12s;
        }

        .pq-textarea:focus {
          border-color: ${BLUE};
        }

        .pq-btn-primary {
          display: block;
          width: 100%;
          padding: 15px;
          background: ${BLUE};
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          margin-top: 16px;
          transition: background 0.15s;
          font-family: 'Inter', sans-serif;
        }

        .pq-btn-primary:hover {
          background: ${NAVY};
        }

        .pq-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pq-form-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #4A6080;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .pq-form-input {
          width: 100%;
          padding: 12px 14px;
          border: 1.5px solid #D6E8F7;
          border-radius: 10px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          color: ${NAVY};
          outline: none;
          transition: border-color 0.12s;
          box-sizing: border-box;
        }

        .pq-form-input:focus {
          border-color: ${BLUE};
        }

        .pq-privacy-note {
          font-size: 12px;
          color: #7A94B0;
          text-align: center;
          margin-top: 12px;
        }

        @media (prefers-reduced-motion: reduce) {
          .pq-progress-fill, .pq-option, .pq-btn-primary { transition: none; }
        }
      `}</style>
    );
  }

  renderHeader() {
    return (
      <div className="pq-header">
        <img src={CONFIG.logoSrc} alt="Logo" className="pq-logo" />
      </div>
    );
  }

  renderProgressBar() {
    const progress = this.getMomentumProgress();
    return (
      <div className="pq-progress-wrap">
        <div className="pq-progress-track">
          <div className="pq-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  }

  renderQuestion() {
    const { currentQuestion, currentAnswer, submitting } = this.state;
    if (!currentQuestion) return null;

    const selectedValues = currentAnswer || [];
    const charCount = (currentAnswer || '').length;

    return (
      <div className="pq-body">
        <h4 className="pq-question">{currentQuestion.question}</h4>
        {currentQuestion.subtitle && (
          <p className="pq-subtitle">{currentQuestion.subtitle}</p>
        )}

        {currentQuestion.type !== 'textarea' && (
          <div style={{ marginTop: 8 }}>
            {currentQuestion.options.map(option => {
              const isSelected = currentQuestion.type === 'multi' && selectedValues.includes(option);
              return (
                <button
                  key={option}
                  className={`pq-option${isSelected ? ' selected' : ''}`}
                  disabled={submitting}
                  onClick={() =>
                    currentQuestion.type === 'multi'
                      ? this.toggleMultiChoice(option)
                      : this.handleSingleChoice(option)
                  }
                >
                  <span>{option}</span>
                  {isSelected && <i className="bi bi-check-circle-fill" style={{ color: BLUE }} />}
                </button>
              );
            })}
          </div>
        )}

        {currentQuestion.type === 'textarea' && (
          <div style={{ position: 'relative', marginTop: 8 }}>
            <textarea
              className="pq-textarea"
              rows={7}
              placeholder="Optional — only if you'd like to share something."
              maxLength={250}
              value={currentAnswer || ''}
              onChange={e => this.setState({ currentAnswer: e.target.value })}
            />
            <span style={{ position: 'absolute', top: 10, right: 14, fontSize: 12, color: '#7A94B0' }}>
              {250 - charCount}
            </span>
          </div>
        )}

        {(currentQuestion.type === 'multi' || currentQuestion.type === 'textarea') && (
          <button
            className="pq-btn-primary"
            onClick={this.handleContinue}
            disabled={submitting || (currentQuestion.type === 'multi' && selectedValues.length === 0)}
          >
            {submitting
              ? <span className="spinner-border spinner-border-sm" role="status" />
              : 'Continue'}
          </button>
        )}
      </div>
    );
  }

  renderContactForm() {
    const { firstName, lastName, email, phone, contactSubmitting } = this.state;
    const canSubmit = firstName && lastName && email && phone;

    return (
      <div className="pq-body">
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h4 className="pq-question" style={{ marginBottom: 8 }}>Almost there!</h4>
          <p style={{ fontSize: 15, color: '#4A6080', margin: 0 }}>
            Leave us your contact details and we'll reach out shortly.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label className="pq-form-label">First Name</label>
          <input
            type="text"
            className="pq-form-input"
            value={firstName}
            onChange={e => this.setState({ firstName: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label className="pq-form-label">Last Name</label>
          <input
            type="text"
            className="pq-form-input"
            value={lastName}
            onChange={e => this.setState({ lastName: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label className="pq-form-label">Email</label>
          <input
            type="email"
            className="pq-form-input"
            value={email}
            onChange={e => this.setState({ email: e.target.value })}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <label className="pq-form-label">Phone</label>
          <input
            type="tel"
            className="pq-form-input"
            value={phone}
            onChange={e => this.setState({ phone: e.target.value })}
          />
        </div>

        <button
          className="pq-btn-primary"
          onClick={this.handleSubmit}
          disabled={!canSubmit || contactSubmitting}
          style={{ marginTop: 24 }}
        >
          {contactSubmitting
            ? <span className="spinner-border spinner-border-sm" role="status" />
            : 'Submit'}
        </button>

        <p className="pq-privacy-note">
          Your information is kept private and will only be used to contact you about this inquiry.
        </p>
      </div>
    );
  }

  renderThankYou() {
    return (
      <div className="pq-root">
        {this.renderStyles()}
        <div className="pq-page" style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '48px 32px' }}>
          <img src={CONFIG.logoSrc} alt="Logo" className="pq-logo" style={{ marginBottom: 32 }} />
          <div style={{ fontSize: 48, marginBottom: 20 }}>🙌</div>
          <h4 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: NAVY, marginBottom: 12 }}>
            You're all set!
          </h4>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 16, color: '#4A6080', maxWidth: 300, margin: '0 auto' }}>
            {CONFIG.thankYouText}
          </p>
        </div>
      </div>
    );
  }

  renderLoading() {
    return (
      <div className="pq-body" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner-border" style={{ color: BLUE }} role="status" />
      </div>
    );
  }

  render() {
    const { quizState, submitted, submitting } = this.state;
    if (submitted) return this.renderThankYou();

    return (
      <div className="pq-root">
        {this.renderStyles()}
        <div className="pq-page">
          {this.renderHeader()}
          {this.renderProgressBar()}
          {quizState === 'loading' && this.renderLoading()}
          {quizState === 'in_progress' && this.renderQuestion()}
          {quizState === 'accepted' && this.renderContactForm()}
          {quizState === 'error' && (
            <div className="pq-body" style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <p style={{ color: '#7A94B0', fontSize: 15 }}>
                Something went wrong. Please refresh the page and try again.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ProQuiz;
